import React, { useRef, useState } from "react";
import { AllCorpusIcon, Button, MedicalIcon, TextInput } from "design_system";
import {
  ComposedDropdownItemProps,
  Dropdown,
} from "components/CoDropdown/ComposedDropdown";
import { ChevronDown, Upload, File, FolderClosed } from "lucide-react";
import { Placement } from "@floating-ui/react";
import { toast } from "react-toastify";
import useLabels from "hooks/useLabels";
import { useFuzzySearch } from "hooks/useFuzzySearch";
import { DataSource, DataSourceType } from "./types";
import useDataSources from "./hooks/useDataSources";
import { useAppSelector, useAppDispatch } from "hooks/useStore";
import { selectSources, addSource, removeSource } from "store/slices/sources";
import { useDataSourceUpload } from "./hooks/useDataSourceUpload";
import { useDragAndDropUpload } from "./hooks/useDragAndDropUpload";
import { DragDropOverlay } from "./components/DragDropOverlay";
import { LoadingIcon } from "components/LoadingIcon";

const MAX_SOURCES = 5;

type CorpusDropdownItemProps = ComposedDropdownItemProps & {
  id: CorpusType;
};

type DataSourceDropdownItemProps = DataSource & ComposedDropdownItemProps;

export type DataSourceSelectorProps = {
  isLoading?: boolean;
  iconOnly?: boolean;
  dropdownPlacement?: Placement;
};

export enum CorpusType {
  ALL = "ALL",
  MEDICAL = "MEDICAL",
}

// Icon mapping for data source types
const DATA_SOURCE_ICON_MAP: Record<DataSourceType, React.ReactNode> = {
  paper: <File size={20} />,
  "uploading-paper": <LoadingIcon size={20} className="text-fg-base" />,
  collection: <FolderClosed size={20} />,
};

// Icons for corpus types - static since they can't be in labels.json
const CORPUS_ICONS = {
  [CorpusType.ALL]: <AllCorpusIcon />,
  [CorpusType.MEDICAL]: <MedicalIcon />,
};

const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  isLoading = false,
  iconOnly = false,
  dropdownPlacement = "bottom-start",
}) => {
  const [labels] = useLabels("features.threads.data-source-selector");
  const dispatch = useAppDispatch();
  const selectedSources = useAppSelector(selectSources);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { dataSources: allDataSources, isLoading: isLoadingDataSources } =
    useDataSources();
  const filteredDataSources = useFuzzySearch(
    allDataSources,
    "title",
    searchQuery
  );

  // Handle paper upload and auto-selection
  const { triggerFileSelection, uploadFile, isUploading } =
    useDataSourceUpload();

  // Handle drag-and-drop upload
  const { isDragging } = useDragAndDropUpload({
    onFileDropped: uploadFile,
  });

  // Build corpus config from labels
  const corpusTypeConfig = {
    [CorpusType.ALL]: {
      label: labels?.["all-corpus"]?.title,
      description: labels?.["all-corpus"]?.description,
      icon: CORPUS_ICONS[CorpusType.ALL],
    },
    [CorpusType.MEDICAL]: {
      label: labels?.medical?.title,
      description: labels?.medical?.description,
      icon: CORPUS_ICONS[CorpusType.MEDICAL],
    },
  };

  const corpusTypes: CorpusDropdownItemProps[] = [
    {
      id: CorpusType.ALL,
      title: corpusTypeConfig[CorpusType.ALL].label,
      description: corpusTypeConfig[CorpusType.ALL].description,
      icon: corpusTypeConfig[CorpusType.ALL].icon,
    },
    {
      id: CorpusType.MEDICAL,
      title: corpusTypeConfig[CorpusType.MEDICAL].label,
      description: corpusTypeConfig[CorpusType.MEDICAL].description,
      icon: corpusTypeConfig[CorpusType.MEDICAL].icon,
    },
  ];

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCorpusType, setSelectedCorpusType] = useState<CorpusType>(
    CorpusType.ALL
  );

  const handleCorpusTypeSelect = (corpusType: CorpusType) => {
    setSelectedCorpusType(corpusType);
  };

  const handleSourceToggle = (source: DataSourceDropdownItemProps) => {
    // Only handle paper sources for now, collections are noop
    if (source.type === "collection" || !source.paper) {
      return;
    }

    const isSelected = selectedSources.some(
      (s) => s.paper_id === source.paper?.paper_id
    );

    if (isSelected) {
      dispatch(removeSource(source.paper.paper_id));
    } else {
      // Check if user is trying to add more than 5 sources
      if (selectedSources.length >= MAX_SOURCES) {
        toast.warning(labels?.["max-sources-warning"]);
        return;
      }
      dispatch(addSource(source.paper));
    }
  };

  const handleUploadClick = () => {
    triggerFileSelection();
  };

  return (
    <>
      <div className="relative" data-intercom-target="Data source selector">
        <Dropdown
          scrollable
          triggerRef={triggerRef}
          open={isDropdownOpen}
          onOpenChange={setIsDropdownOpen}
          placement={dropdownPlacement}
          mobileTitle={labels["mobile-title"]}
        >
          {/* Temporarily disable corpus types until we implement doctor mode */}
          {/* <Dropdown.Section>
          {corpusTypes.map((corpusType) => (
            <Dropdown.RadioItem
              key={corpusType.id}
              title={corpusType.title}
              description={corpusType.description}
              icon={corpusType.icon}
              isActive={selectedCorpusType === corpusType.id}
              onClick={() => handleCorpusTypeSelect(corpusType.id)}
            />
          ))}
        </Dropdown.Section> */}
          <Dropdown.Section showBorder={false}>
            <Dropdown.SectionHeader title={labels["add-sources-title"]} />
            <>
              <div className="px-2 py-1">
                <TextInput
                  id="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="sm"
                  placeholder={labels["search-placeholder"]}
                />
              </div>
              {filteredDataSources.map((dataSource) => (
                <Dropdown.CheckboxItem
                  key={dataSource.id}
                  title={dataSource.title}
                  icon={DATA_SOURCE_ICON_MAP[dataSource.type]}
                  description={dataSource.description}
                  isActive={
                    dataSource.type === "uploading-paper" ||
                    (dataSource.type === "paper" &&
                      selectedSources.some(
                        (source) =>
                          source.paper_id === dataSource.paper?.paper_id
                      ))
                  }
                  onClick={() =>
                    dataSource.type !== "uploading-paper" &&
                    handleSourceToggle(
                      dataSource as DataSourceDropdownItemProps
                    )
                  }
                  isDisabled={dataSource.type === "uploading-paper"}
                />
              ))}
            </>
          </Dropdown.Section>
          <Dropdown.Footer
            primaryAction={{
              label: isUploading
                ? labels["uploading-paper"]
                : labels["upload-papers"],
              icon: <Upload />,
              onClick: handleUploadClick,
              disabled: isUploading,
            }}
          />
        </Dropdown>

        <Button
          ref={triggerRef}
          variant="flat"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            setIsDropdownOpen(!isDropdownOpen);
          }}
          iconLeft={corpusTypeConfig[selectedCorpusType].icon}
          iconRight={<ChevronDown />}
          iconOnly={iconOnly}
          disabled={isLoading || isLoadingDataSources}
          data-testid="data-source-selector-button"
        >
          {corpusTypeConfig[selectedCorpusType].label}
        </Button>
      </div>

      {/* Drag and drop overlay */}
      <DragDropOverlay isVisible={isDragging} />
    </>
  );
};

export default DataSourceSelector;
